import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Feedback, FeedbackThreadWithMessages, FeedbackThreadMessage, Artist } from '../../types/artist';
import { feedbackThreadApi } from '../../services/feedbackThreadApi';
import { ArtistOverview } from './ArtistOverview';
import { useAuth } from '../../contexts/AuthContext';
import { useThreadMessageSubscription } from '../../hooks/useNotificationWebSocket';
import './FeedbackThreadInline.css';

interface FeedbackThreadInlineProps {
  feedback: Feedback;
  isReviewer: boolean; // true if current user is the reviewer, false if artist
}

export const FeedbackThreadInline: React.FC<FeedbackThreadInlineProps> = ({
  feedback,
  isReviewer,
}) => {
  const { user } = useAuth();
  const [threadData, setThreadData] = useState<FeedbackThreadWithMessages | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [showRevealDialog, setShowRevealDialog] = useState(false);
  const [isRevealing, setIsRevealing] = useState(false);
  const [reviewerArtist, setReviewerArtist] = useState<Artist | null>(null);
  const [showReviewerModal, setShowReviewerModal] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const loadThread = useCallback(async (page?: number) => {
    if (!feedback.id) return;

    try {
      setLoading(true);
      setError(null);
      
      // If no page specified, load page 1 first to get total pages, then load last page
      if (page === undefined) {
        const initialData = await feedbackThreadApi.getThreadWithMessages(feedback.id, 1, 6);
        const lastPage = initialData.messages.totalPages;
        
        // If there's more than one page, load the last page
        if (lastPage > 1) {
          const lastPageData = await feedbackThreadApi.getThreadWithMessages(feedback.id, lastPage, 6);
          setThreadData(lastPageData);
          setCurrentPage(lastPage);
        } else {
          setThreadData(initialData);
          setCurrentPage(1);
        }
      } else {
        const data = await feedbackThreadApi.getThreadWithMessages(feedback.id, page, 6);
        setThreadData(data);
        setCurrentPage(page);
      }
    } catch (err) {
      console.error('Failed to load thread:', err);
      setError('Failed to load conversation. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [feedback.id]);

  useEffect(() => {
    loadThread();
  }, [loadThread]);

  // Load reviewer artist info when thread is not anonymous
  useEffect(() => {
    const loadReviewerArtist = async () => {
      // Only load if thread data exists, thread is not anonymous (anonymous === false), user is not the reviewer, and we have a feedback ID
      if (threadData && threadData.anonymous === false && !isReviewer && feedback.id) {
        try {
          const artist = await feedbackThreadApi.getReviewerArtist(feedback.id);
          setReviewerArtist(artist);
        } catch (err) {
          console.error('Failed to load reviewer artist:', err);
          // Clear reviewer artist on error
          setReviewerArtist(null);
        }
      } else {
        // Clear reviewer artist if conditions aren't met
        setReviewerArtist(null);
      }
    };

    loadReviewerArtist();
  }, [threadData, threadData?.anonymous, isReviewer, feedback.id]);

  // Subscribe to real-time thread messages via WebSocket
  useThreadMessageSubscription(
    useCallback((data) => {
      // Only handle messages for this specific thread
      if (data.feedbackId === feedback.id && threadData) {
        // Check if this message is already in our list (avoid duplicates)
        const messageExists = threadData.messages.items.some(
          (msg) => msg.id === data.message.id
        );
        
        if (!messageExists) {
          // Check if we're on the last page
          const isOnLastPage = currentPage === threadData.messages.totalPages;
          
          if (isOnLastPage) {
            // Add the message to the current view
            setThreadData((prev) => {
              if (!prev) return prev;
              
              return {
                ...prev,
                messages: {
                  ...prev.messages,
                  items: [...prev.messages.items, data.message],
                  totalCount: prev.messages.totalCount + 1,
                },
              };
            });
          } else {
            // User is viewing an older page, just update the total count
            setThreadData((prev) => {
              if (!prev) return prev;
              
              return {
                ...prev,
                messages: {
                  ...prev.messages,
                  totalCount: prev.messages.totalCount + 1,
                  totalPages: Math.ceil((prev.messages.totalCount + 1) / prev.messages.pageSize),
                },
              };
            });
          }
        }
      }
    }, [feedback.id, threadData, currentPage])
  );

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && threadData?.messages.items.length) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [threadData?.messages.items.length]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim() || !feedback.id || submitting) return;

    // Check if thread is closed
    if (threadData?.closed) {
      setError('This conversation has been closed.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      
      await feedbackThreadApi.postMessage(feedback.id, {
        message: message.trim(),
      });

      setMessage('');
      
      // Store the old total pages count before reloading
      const oldTotalPages = threadData?.messages.totalPages || 1;
      
      // Reload thread on current page to get updated data
      const updatedData = await feedbackThreadApi.getThreadWithMessages(feedback.id, currentPage, 6);
      
      // Check if a new page was created
      if (updatedData.messages.totalPages > oldTotalPages) {
        // A new page was created, navigate to the last page
        await loadThread(updatedData.messages.totalPages);
      } else {
        // No new page, just update with current page data
        setThreadData(updatedData);
        setCurrentPage(currentPage);
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseThread = async () => {
    if (!feedback.id || !threadData) return;

    try {
      setIsClosing(true);
      setError(null);
      await feedbackThreadApi.closeThread(feedback.id, threadData.anonymous);
      await loadThread(currentPage);
      setShowCloseDialog(false);
    } catch (err) {
      console.error('Failed to close thread:', err);
      setError('Failed to close conversation. Please try again.');
    } finally {
      setIsClosing(false);
    }
  };

  const handleRevealIdentity = async () => {
    if (!feedback.id || !threadData) return;

    try {
      setIsRevealing(true);
      setError(null);
      await feedbackThreadApi.revealIdentity(feedback.id, threadData.closed);
      await loadThread(currentPage);
      setShowRevealDialog(false);
    } catch (err) {
      console.error('Failed to reveal identity:', err);
      setError('Failed to reveal identity. Please try again.');
    } finally {
      setIsRevealing(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    loadThread(newPage);
  };

  const isUserMessage = (msg: FeedbackThreadMessage): boolean => {
    // If thread is anonymous and message userId is "anonymous", 
    // then it's from the reviewer
    if (threadData?.anonymous && msg.userId === 'anonymous') {
      return isReviewer;
    }
    
    // Otherwise check if the userId matches the current user
    return msg.userId === user?.id;
  };

  const getMessageAuthor = (msg: FeedbackThreadMessage): string => {
    if (isUserMessage(msg)) {
      return 'You';
    }
    
    // If reviewer has revealed identity, show their name
    // Otherwise show "Anonymous Reviewer" or "Artist"
    if (isReviewer) {
      return 'Artist';
    } else {
      if (threadData?.anonymous === false && reviewerArtist) {
        return reviewerArtist.name;
      }
      return 'Anonymous Reviewer';
    }
  };

  const handleReviewerClick = () => {
    if (!threadData?.anonymous && reviewerArtist && !isReviewer) {
      setShowReviewerModal(true);
    }
  };

  if (loading && !threadData) {
    return (
      <div className="feedback-thread-inline">
        <div className="thread-loading">Loading conversation...</div>
      </div>
    );
  }

  return (
    <div className="feedback-thread-inline">
      {/* Status Badges */}
      {(threadData?.closed || (threadData?.anonymous && isReviewer) || (!threadData?.anonymous && !isReviewer)) && (
        <div className="thread-status-badges">
          {threadData?.closed && <span className="status-badge closed">Closed</span>}
          {threadData?.anonymous && isReviewer && (
            <span className="status-badge anonymous">Anonymous</span>
          )}
          {!threadData?.anonymous && !isReviewer && (
            <span className="status-badge revealed">Identity Revealed</span>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="thread-error">
          <p>{error}</p>
        </div>
      )}

      {/* Messages */}
      <div className="thread-messages-inline">
        {threadData?.messages.items.length === 0 ? (
          <div className="no-messages">
            <p>No messages yet. Start the conversation below!</p>
          </div>
        ) : (
          <>
            {threadData?.messages.items.map((msg) => (
              <div
                key={msg.id}
                className={`message-inline ${isUserMessage(msg) ? 'message-own' : 'message-other'}`}
              >
                <div className="message-header-inline">
                  <span 
                    className={`message-author-inline ${!threadData?.anonymous && !isReviewer && !isUserMessage(msg) ? 'clickable-author' : ''}`}
                    onClick={() => {
                      if (!threadData?.anonymous && !isReviewer && !isUserMessage(msg)) {
                        handleReviewerClick();
                      }
                    }}
                  >
                    {getMessageAuthor(msg)}
                  </span>
                  <span className="message-time-inline">
                    {new Date(msg.createdAt).toLocaleString()}
                  </span>
                </div>
                <div className="message-content-inline">
                  {msg.message}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Pagination */}
      {threadData && threadData.messages.totalPages > 1 && (
        <div className="thread-pagination-inline">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={!threadData.messages.hasPreviousPage}
            className="pagination-button-inline"
          >
            ← Previous
          </button>
          <span className="page-info-inline">
            Page {threadData.messages.page} of {threadData.messages.totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={!threadData.messages.hasNextPage}
            className="pagination-button-inline"
          >
            Next →
          </button>
        </div>
      )}

      {/* Message Input */}
      {!threadData?.closed ? (
        <form className="thread-input-form-inline" onSubmit={handleSendMessage}>
          <textarea
            className="message-input-inline"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your message..."
            rows={3}
            maxLength={200}
            disabled={submitting}
          />
          <div className="thread-actions-inline">
            <div className="thread-action-buttons-inline">
              {isReviewer && threadData?.anonymous && (
                <button
                  type="button"
                  className="reveal-button-inline"
                  onClick={() => setShowRevealDialog(true)}
                  disabled={submitting}
                >
                  Reveal Identity
                </button>
              )}
              <button
                type="button"
                className="close-thread-button-inline"
                onClick={() => setShowCloseDialog(true)}
                disabled={submitting}
              >
                Close Conversation
              </button>
            </div>
            <button
              type="submit"
              className="send-button-inline"
              disabled={!message.trim() || submitting}
            >
              {submitting ? 'Sending...' : 'Send'}
            </button>
          </div>
        </form>
      ) : (
        <div className="thread-closed-notice-inline">
          <p>This conversation has been closed. No more messages can be added.</p>
        </div>
      )}

      {/* Close Thread Confirmation Dialog */}
      {showCloseDialog && (
        <div className="close-dialog-overlay">
          <div className="close-dialog">
            <h3>Close Conversation</h3>
            <p>Are you sure you want to close this conversation?</p>
            <p className="warning-text">This is a PERMANENT action. Once closed, neither you nor the other person will be able to add more messages.</p>
            <div className="dialog-actions">
              <button 
                className="cancel-button" 
                onClick={() => setShowCloseDialog(false)}
                disabled={isClosing}
              >
                Cancel
              </button>
              <button 
                className="close-confirm-button" 
                onClick={handleCloseThread}
                disabled={isClosing}
              >
                {isClosing ? 'Closing...' : 'Close Conversation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reveal Identity Confirmation Dialog */}
      {showRevealDialog && (
        <div className="close-dialog-overlay">
          <div className="close-dialog">
            <h3>Reveal Identity</h3>
            <p>This will reveal your Artist Profile to the other artist in the chat.</p>
            <div className="dialog-actions">
              <button 
                className="cancel-button" 
                onClick={() => setShowRevealDialog(false)}
                disabled={isRevealing}
              >
                Cancel
              </button>
              <button 
                className="close-confirm-button" 
                onClick={handleRevealIdentity}
                disabled={isRevealing}
              >
                {isRevealing ? 'Revealing...' : 'Reveal Identity'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reviewer Artist Modal */}
      {showReviewerModal && reviewerArtist && (
        <ArtistOverview
          artist={reviewerArtist}
          showAsModal={true}
          onClose={() => setShowReviewerModal(false)}
        />
      )}
    </div>
  );
};
